import * as Y from 'yjs'

// Entity arrays keep their objects in a map, so moving a scene never destroys
// its shared text. Only the ordered IDs move through the CRDT array.
const textKeys = new Set(['title', 'name', 'label', 'text', 'notes', 'synopsis', 'chapter', 'summary', 'body', 'description', 'voice', 'role', 'premise', 'world', 'direction', 'speaker', 'alt', 'caption', 'relationship'])
const equal = (a, b) => JSON.stringify(a) === JSON.stringify(b)
function defaultsFor(value) {
  let defaults = {}
  if (value.version === 1 && Array.isArray(value.nodes)) defaults = { bible: { premise: '', world: '', notes: '', characters: [] }, variables: [] }
  else if (Array.isArray(value.blocks) && Array.isArray(value.choices)) defaults = { chapter: '', synopsis: '', notes: '', effects: [] }
  else if (value.kind === 'dialogue' && typeof value.text === 'string') defaults = { speaker: '', direction: '' }
  else if (Array.isArray(value.characters)) defaults = { premise: '', world: '', notes: '', entries: [], sections: [], links: [], parents: [] }
  else if (typeof value.sectionId === 'string') defaults = { summary: '', body: '', images: [], audio: [], references: [] }
  else if (typeof value.name === 'string' && ('voice' in value || 'role' in value)) defaults = { role: '', voice: '', description: '', images: [], audio: [], references: [] }
  else if ('targetId' in value && 'label' in value) defaults = { conditions: [], effects: [] }
  else if ('dataUrl' in value) defaults = { alt: '' }
  return defaults
}
export function sharedDefaults(value) {
  if (Array.isArray(value)) return value.map(sharedDefaults)
  if (!value || typeof value !== 'object') return value
  return Object.fromEntries(Object.entries({ ...defaultsFor(value), ...value }).filter(([, v]) => v !== undefined).map(([key, v]) => [key, sharedDefaults(v)]))
}
const snapshots = new WeakMap(), observed = new WeakSet()
function observeSnapshots(doc) {
  if (observed.has(doc)) return
  observed.add(doc)
  const root = doc.getMap('root')
  root.observeDeep(events => {
    for (const event of events) {
      let item = root
      snapshots.delete(item)
      for (const key of event.path) {
        item = item?.get?.(key)
        if (item && typeof item === 'object') snapshots.delete(item)
      }
      snapshots.delete(event.target)
    }
  })
}
function encode(value, key = '') {
  if (typeof value === 'string' && textKeys.has(key)) return new Y.Text(value)
  if (Array.isArray(value)) {
    if (value.every(v => v && typeof v.id === 'string')) {
      const list = new Y.Map()
      list.set('$list', true)
      const order = new Y.Array(), items = new Y.Map()
      order.push(value.map(v => v.id))
      for (const item of value) items.set(item.id, encode(item))
      list.set('order', order); list.set('items', items)
      return list
    }
    // Small value arrays (e.g. parent relationships) are atomic values.
    return structuredClone(value)
  }
  if (value && typeof value === 'object') {
    const map = new Y.Map()
    for (const [k, v] of Object.entries(value)) if (v !== undefined) map.set(k, encode(v, k))
    return map
  }
  return value
}
export function readShared(value, reuse = false) {
  if (value instanceof Y.Text) return value.toString()
  if (value instanceof Y.Map) {
    if (reuse && snapshots.has(value)) return snapshots.get(value)
    let result
    if (value.get('$list') === true) {
      const items = value.get('items')
      result = [...new Set(value.get('order').toArray())].filter(id => items.has(id)).map(id => readShared(items.get(id), reuse))
    } else result = Object.fromEntries([...value.entries()].map(([k, v]) => [k, readShared(v, reuse)]))
    if (reuse) snapshots.set(value, result)
    return result
  }
  return value
}
function update(parent, key, next) {
  const current = parent.get(key)
  if (readShared(current, true) === next) return
  if (current instanceof Y.Text && typeof next === 'string') {
    const old = current.toString()
    if (old === next) return
    let start = 0, end = 0
    while (start < old.length && start < next.length && old[start] === next[start]) start++
    while (end < old.length - start && end < next.length - start && old[old.length - 1 - end] === next[next.length - 1 - end]) end++
    if (old.length - start - end) current.delete(start, old.length - start - end)
    if (next.length - start - end) current.insert(start, next.slice(start, next.length - end))
  } else if (current instanceof Y.Map && current.get('$list') && Array.isArray(next) && next.every(v => v && typeof v.id === 'string')) {
    const order = current.get('order'), items = current.get('items'), ids = next.map(v => v.id)
    for (const id of [...items.keys()]) if (!ids.includes(id)) items.delete(id)
    for (const item of next) update(items, item.id, item)
    // An ID may appear twice after two concurrent moves; reads deduplicate it.
    for (let i = order.length - 1; i >= 0; i--) if (!ids.includes(order.get(i)) || order.toArray().indexOf(order.get(i)) !== i) order.delete(i, 1)
    for (let i = 0; i < ids.length; i++) {
      if (order.get(i) === ids[i]) continue
      const at = order.toArray().indexOf(ids[i], i + 1)
      if (at >= 0) order.delete(at, 1)
      order.insert(i, [ids[i]])
    }
  } else if (current instanceof Y.Map && !current.get('$list') && next && typeof next === 'object' && !Array.isArray(next)) {
    next = { ...defaultsFor(next), ...next }
    for (const k of [...current.keys()]) if (next[k] === undefined) current.delete(k)
    for (const [k, v] of Object.entries(next)) if (v !== undefined) update(current, k, v)
  } else if (!equal(readShared(current), next)) parent.set(key, encode(sharedDefaults(next), key))
}
export function writeShared(doc, project, origin = null) {
  observeSnapshots(doc)
  doc.transact(() => update(doc.getMap('root'), 'project', project), origin)
}
export const sharedProject = doc => { observeSnapshots(doc); return readShared(doc.getMap('root').get('project')) }
export const sharedSnapshot = doc => { observeSnapshots(doc); return readShared(doc.getMap('root').get('project'), true) }
export function sharedText(doc, path) {
  let value = doc.getMap('root').get('project')
  for (const key of path) {
    if (!(value instanceof Y.Map)) return null
    value = value.get('$list') ? value.get('items').get(key) : value.get(key)
  }
  return value instanceof Y.Text ? value : null
}
export function toBase64(bytes) {
  if (typeof Buffer !== 'undefined') return Buffer.from(bytes).toString('base64')
  if (typeof bytes.toBase64 === 'function') return bytes.toBase64()
  const chunks = []
  for (let offset = 0; offset < bytes.length; offset += 32768) chunks.push(String.fromCharCode(...bytes.subarray(offset, offset + 32768)))
  return btoa(chunks.join(''))
}
export const fromBase64 = value => typeof Buffer !== 'undefined' ? new Uint8Array(Buffer.from(value, 'base64')) : Uint8Array.from(atob(value), c => c.charCodeAt(0))
export function loadShared(record) {
  const doc = new Y.Doc()
  if (record.live) {
    Y.applyUpdate(doc, fromBase64(record.live.snapshot))
    for (const update of record.live.updates) Y.applyUpdate(doc, fromBase64(update))
  } else writeShared(doc, record.project)
  return doc
}
export function sharedStorage(doc, previous) {
  if (!previous) return { snapshot: toBase64(Y.encodeStateAsUpdate(doc)), updates: [] }
  const old = loadShared({ live: previous })
  const delta = Y.encodeStateAsUpdate(doc, Y.encodeStateVector(old))
  old.destroy()
  const updates = [...previous.updates, toBase64(delta)]
  if (updates.length >= 128 || updates.reduce((n, v) => n + v.length, 0) > 512 * 1024)
    return { snapshot: toBase64(Y.encodeStateAsUpdate(doc)), updates: [] }
  return { snapshot: previous.snapshot, updates }
}
